import { useEffect, useRef, useState } from "react";
import { useApp } from "../../context/AppState";
import { usePopup } from "../../hooks/usePopup";
import cartService from "../../services/cart.service";
import {
  generateDummyProducts,
  getDummyProductCount,
  shouldShowDummyProducts,
} from "../../utils/dummyProductGenerator";
import { shouldSkipReactRuntimeOfferFetch } from "../../utils/popup-runtime-content-loading";

/**
 * Loads the single product shown in the live cart drawer offer (same rules as ProductBlock).
 */
export function useCartDrawerProduct({
  popupData,
  popupState,
  productSection,
  activeStep,
}) {
  const { appState } = useApp();
  const { fetchRandomProduct } = usePopup();
  const [products, setProducts] = useState(() =>
    Array.isArray(productSection?.products) ? productSection.products : []
  );
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    hasFetchedRef.current = false;
    setProducts(
      Array.isArray(productSection?.products) ? productSection.products : []
    );
  }, [productSection?.products, productSection?._id]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const productsToOffer =
        popupData?.generalSettings?.condition?.product?.products_to_offer;
      const shouldShowDummy =
        appState?.is_preview_mode &&
        shouldShowDummyProducts(productsToOffer?.type);

      if (shouldShowDummy) {
        const count = getDummyProductCount(popupData, activeStep);
        setProducts(generateDummyProducts(count));
        return;
      }

      if (
        shouldSkipReactRuntimeOfferFetch({
          isPreviewMode: appState?.is_preview_mode,
        })
      ) {
        const hydrated = Array.isArray(productSection?.products)
          ? productSection.products
          : [];
        if (hydrated.length > 0) {
          setProducts(hydrated);
        }
        return;
      }

      if (!popupState?.openPopup || !productSection) {
        return;
      }

      const stub = productSection?.products?.[0];
      if (
        stub?.handle &&
        (!Array.isArray(stub.variants) || stub.variants.length === 0)
      ) {
        setIsLoading(true);
        try {
          const full = await cartService.fetchProductByHandle(stub.handle);
          if (!cancelled) {
            setProducts([{ ...stub, ...full }]);
          }
        } catch (error) {
          console.error("[CartDrawer] Product fetch by handle failed", error);
          if (!cancelled && stub) setProducts([stub]);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
        return;
      }

      if (
        appState?.is_preview_mode ||
        !productsToOffer?.type ||
        hasFetchedRef.current
      ) {
        return;
      }

      const current = Array.isArray(productSection?.products)
        ? productSection.products
        : [];
      if (current.length > 0 && current[0]?.variants?.length > 0) {
        return;
      }

      hasFetchedRef.current = true;
      setIsLoading(true);

      try {
        const productCount =
          popupData?.generalSettings?.condition?.product
            ?.products_to_offer_limit || 1;
        const allowMultiple =
          popupData?.generalSettings?.condition?.product
            ?.allowMultipleProducts;

        const selected = await fetchRandomProduct(
          productsToOffer,
          false,
          productCount,
          allowMultipleProducts,
          popupData,
          {
            preferCart: true,
            surface: "cart-drawer",
          }
        );

        if (cancelled) return;

        if (selected) {
          setProducts(
            allowMultiple && Array.isArray(selected) ? selected : [selected]
          );
        } else if (productSection?.products?.length) {
          setProducts(productSection.products);
        }
      } catch (error) {
        console.error("[CartDrawer] Product offer fetch failed", error);
        if (!cancelled && productSection?.products?.length) {
          setProducts(productSection.products);
        }
        hasFetchedRef.current = false;
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    popupData,
    popupState?.openPopup,
    productSection,
    activeStep,
    appState?.is_preview_mode,
  ]);

  return {
    product: products[0] || null,
    isLoading,
  };
}
